import { Box, styled } from '@mui/material';
import { SxProps, Theme } from '@mui/system';
import { gridClasses } from '@mui/x-data-grid';
import { ClientDTO, ClientStatusDTO, ILocation, isoCountries } from 'wisestep-api';
import { WsColors } from 'wisestep-base';

import { IBreadCrumbsList } from '../../../components/WsBreadCrumbs';
import getEncodedQueryParams from '../../../utils/getEncodedQueryParams';
import { legalIdentifierDefaultPayload } from '../../clientsSettings/helper/ClientSettingsHelper';

export const defaultClientStatusDTO: ClientStatusDTO = {
  id: '',
  clientStatusName: '',
};

export const defaultClientDTO: ClientDTO = {
  name: '',
  legalName: '',
  industry: '',
  website: '',
  timezone: '',
  invoiceCode: '',
  paymentTerms: '',
  address: '',
  description: '',
  createdByName: '',
  country: '',
  branch: '',
  source: '',
  isActive: true,
  id: '',
  parentId: '',
  createdBy: '',
  createdOn: null,
  businessUnit: null,
  childCount: 0,
  location: null,
  clientStatusId: '',
  clientStatus: defaultClientStatusDTO,
  clientStatusReason: '',
  clientContactCount: 0,
  tags: [],
};

export const getDefaultClientBank = (clientId: string) => ({
  data: {
    id: '',
    bankName: '',
    branchName: '',
    bankCode: '',
    bankAccountNumber: '',
    currencyId: '',
    iban: '',
    swiftCode: '',
    clientId: clientId ?? '',
    isActive: true,
  },
  isEditable: true,
});

export const defaultLegalData = (clientId: string) => ({
  data: {
    id: null,
    legalIdentifierDTO: { ...legalIdentifierDefaultPayload, name: '', id: '' },
    clientId: clientId ?? '',
    value: '',
    createdBy: '',
    modifiedBy: '',
    modifiedOn: null,
    createdOn: null,
  },
  isEditable: true,
});

export const defaultClientContactDTO = (clientId, input?: string) => ({
  id: null,
  firstName: input?.split(/[ @]/)[0] ?? '',
  lastName: input?.split(' ')[1] ?? '',
  emailAddress: input?.includes('@') ? input : '',
  phoneNumber: '',
  physicalAddress: '',
  position: '',
  linkedinProfile: '',
  supervisorPosition: '',
  supervisorName: '',
  createdByName: '',
  markStatus: true,
  workspaceId: '',
  clientId: clientId ?? '',
  createdBy: '',
  createdOn: null,
  tags: [],
  location: null,
});

export const getClientLocation = (clientDTO: ClientDTO) => {
  let location: string | null = null;
  if (clientDTO.branch) {
    location = clientDTO.branch;
  }
  if (isoCountries[clientDTO.country]) {
    location = (location ? location + ', ' : '') + isoCountries[clientDTO.country];
  }
  return location;
};

export const initialLocation: ILocation = {
  continent: '',
  continentCode: '',
  city: '',
  state: '',
  country: '',
  countryCode: '',
  stateCode: '',
};

export interface IFilters {
  search: string;
  status: string[] | null;
  location?: ILocation[];
  tags: string[];
}

export const defaultFilters: IFilters = {
  search: '',
  status: null,
  location: initialLocation[0],
  tags: [],
};

export const getCommonGridStyles = (): SxProps<Theme> => ({
  [`& .${gridClasses.cell}`]: {
    py: 1,
  },
  [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
    outline: 'none',
  },
  [`& .${gridClasses.row}:hover`]: {
    backgroundColor: 'inherit',
  },
});

export const linkedInRegex =
  /^(https?:\/\/)?([a-z]{2,3}\.)?linkedin\.com\/(in|company)\/(?:[a-zA-Z0-9&-.]+|%[0-8][0-9A-Fa-f])+(\/(?:[a-zA-Z0-9&-.]+|%[0-8][0-9A-Fa-f])*)?\/?(\?.*)?$/;

export const websiteRegex = /^(?:https?:\/\/)?([\w-]+\.)+[\w-]{2,}(:\d+)?(\/\S*)?$/;

export const getEncodedBreadCrumbs = (
  prevBreadCrumbs: IBreadCrumbsList[],
  clientName: string
): string =>
  getEncodedQueryParams([
    ...prevBreadCrumbs,
    { label: clientName, url: `${window.location.pathname}${window.location.search}` },
  ]);

export const StyledBox = styled(Box)(() => ({
  backgroundColor: WsColors.White,
  border: `1px solid ${WsColors.Gray88}`,
  padding: 16,
  borderTopLeftRadius: 2,
  borderTopRightRadius: 2,
  borderBottom: `1px solid ${WsColors.Gray88}`,
}));
